import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddMailPermissionMutation from './AddMailPermissionMutation';
import MailPermissionForm, { MailPermissionData } from './MailPermissionForm';
import UI from '../../../common/UI';
import mailPermissionSchema from './mailPermissionSchema';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

export interface AddMailPermissionFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const AddMailPermissionForm = ({
  onSuccess, onCancel,
}: AddMailPermissionFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();

  const form = useForm<MailPermissionData>({
    values: {
      title: '',
      title_translations: [],
      label: '',
      label_translations: [],
      events: [],
    },
  });

  const [addMailPermission, { loading, error }] = useMutation(
    AddMailPermissionMutation,
    {
      variables: {
        input: {
          project: { id: project.id },
          ...form.values,
        },
      },
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.mail_permission_added'));
      },
      onError: () => {
        //
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: mailPermissionSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addMailPermission()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_mail_permission')}</UI.H4>

          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_mail_permission_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <MailPermissionForm form={form} errors={errors} project={project} />

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_mail_permission')}
          onClose={onCancel}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default AddMailPermissionForm;
